<template>
  <div style="background:var(--bg); color:var(--ink); min-height:calc(100vh - 86px);">

    <!-- Email not found message -->
    <div v-if="!isValidEmail" style="padding:64px 24px; text-align:center;">
      <h1 style="font-size:36px; font-weight:700; margin:0 0 8px;">Email not found</h1>
      <p style="color:var(--muted); margin:0 0 20px;">
        There is no email "{{ emailAlias }}" in the "{{ groupAlias }}" workspace.
      </p>
      <AppButton variant="primary" to="/app/emails">Back to emails</AppButton>
    </div>

    <!-- Main content when email is valid -->
    <div
        v-else
        class="mx-auto px-4 pb-10 pt-2 md:px-7"
        style="display:flex; flex-wrap:wrap; align-items:flex-start; gap:28px; max-width:1480px;"
    >

      <!-- MAIN COLUMN -->
      <div style="flex:999 1 560px; min-width:0;">

        <!-- Title block + actions -->
        <div style="display:flex; align-items:flex-end; justify-content:space-between; gap:16px; flex-wrap:wrap;">
          <div style="min-width:0;">
            <h1 style="font-size:44px; font-weight:700; letter-spacing:-.025em; line-height:1; margin:0;">
              {{ email?.name || email?.subject || emailAlias }}
            </h1>

            <!-- Status and meta line -->
            <div style="display:flex; flex-wrap:wrap; align-items:center; gap:4px 12px; margin-top:10px; color:var(--muted);">
              <ConsoleBadge :color="email?.web_id ? 'good' : 'warn'">{{ email?.web_id ? 'published' : 'draft' }}</ConsoleBadge>
              <ConsoleBadge v-if="reviewStatus === 'approved'" color="good">approved</ConsoleBadge>
              <ConsoleBadge v-else-if="reviewStatus === 'in_review'" color="cyan">in review</ConsoleBadge>
              <span class="meta-cap">{{ email?.type || 'newsletter' }}</span>
              <span v-if="email?.wrapper">{{ email.wrapper }}</span>
              <span v-if="email?.deployDate">Deploys {{ formatDate(email.deployDate) }}</span>
            </div>
          </div>

          <!-- One primary action: Save while there are unsaved edits, otherwise the campaign. -->
          <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
            <AppButton @click="showSendTestModal = true">Send test</AppButton>
            <AppButton
                v-if="editorRef?.dirty"
                variant="primary"
                :disabled="editorRef.saving"
                @click="editorRef.saveEmail()"
            >
              {{ editorRef.saving ? 'Saving…' : 'Save' }}
            </AppButton>
            <AppButton v-else-if="userCanEdit && !email?.web_id" variant="primary" @click="openCreateCampaignModal">
              Create campaign
            </AppButton>
            <AppButton
                v-else-if="userCanEdit"
                variant="primary"
                :disabled="loading.update"
                @click="updateMailchimpCampaign"
            >
              {{ loading.update ? 'Updating…' : 'Update campaign' }}
            </AppButton>

            <UDropdownMenu
                :items="actionItems"
                :popper="{ placement: 'bottom-end' }"
                :ui="{ width: 'w-48' }"
            >
              <!-- A plain button: the menu needs an element ref for its trigger. -->
              <button type="button" class="btn btn-icon" aria-label="More actions">
                <UIcon name="i-heroicons-ellipsis-horizontal" class="h-5 w-5"/>
              </button>
            </UDropdownMenu>
          </div>
        </div>

        <!-- Tabs -->
        <div style="margin-top:20px;">
          <AppTabs v-model="activeTab" :tabs="tabItems" />

          <!-- Preview Tab -->
          <div v-show="activeTab === 'preview'">
            <!-- missing-size warning: a size the built email needs has no slices → that
                 view shows broken images. Drive the user to slice its design comp. -->
            <div
                v-for="m in missingSizes"
                :key="m.size"
                class="flex items-center gap-2.5"
                style="margin-top:12px; border:1px solid color-mix(in srgb, var(--warn) 35%, transparent); background:color-mix(in srgb, var(--warn) 8%, var(--panel)); border-radius:8px; padding:10px 12px;"
            >
              <UIcon name="i-heroicons-exclamation-triangle" class="h-5 w-5" style="color:var(--warn); flex-shrink:0;"/>
              <div style="flex:1;">
                <div style="font-weight:600; color:var(--warn);">No {{ m.size }} slices. The {{ m.size }} view will show broken images.</div>
                <div style="color:var(--muted);">
                  {{ m.hasComp
                    ? `Slice the ${m.size} design to generate its images.`
                    : `No ${m.size} design is attached. Add one to this email’s source assets, then slice it.` }}
                </div>
              </div>
              <AppButton v-if="m.hasComp" small :to="`/app/emails/${groupAlias}/${emailAlias}/slice`">
                Slice {{ m.size }}
              </AppButton>
            </div>

            <EmailPreview
                v-model:email="email"
                v-model:group="group"
            />
          </div>

          <!-- Results tab -->
          <div v-if="activeTab === 'results'" style="margin-top:16px;">
            <CampaignAnalytics
                v-if="email?.web_id && campaignHashId"
                :campaign-id="campaignHashId"
                :group-alias="groupAlias"
                hide-linked-email
                @loaded="onAnalyticsLoaded"
            />
            <AppLoadingState v-else-if="email?.web_id" variant="kpi" :count="6" label="Finding this email's campaign" slow-hint="Your email platform can take a few seconds to answer." />
            <div v-else class="console-card" style="padding:44px 18px; text-align:center;">
              <UIcon name="i-heroicons-chart-bar" class="h-10 w-10" style="color:var(--faint); margin:0 auto;" />
              <h2 style="margin:12px 0 0; font-size:24px; font-weight:700;">No results yet</h2>
              <p style="margin:6px auto 0; max-width:420px; color:var(--muted);">
                Opens, clicks and link performance appear here once this email has a campaign and it has been sent.
              </p>
              <AppButton
                  v-if="userCanEdit"
                  variant="primary"
                  style="margin-top:18px;"
                  @click="openCreateCampaignModal"
              >
                Create campaign
              </AppButton>
            </div>
          </div>

          <!-- Sections and Details tabs: the editor. Mounted on first visit and kept
               mounted, so unsaved edits survive a switch to another tab. -->
          <div v-if="editorVisited" v-show="activeTab === 'sections' || activeTab === 'details'" style="margin-top:16px;">
            <EmailEditor ref="editorRef" />
          </div>

          <!-- Details also carries the generated plain-text version -->
          <div v-show="activeTab === 'details'" class="console-card" style="padding:16px; margin-top:18px;">
            <div class="flex items-center justify-between" style="margin-bottom:10px;">
              <h2 style="margin:0; font-size:20px; font-weight:700;">Text version</h2>
              <AppButton small @click="copyTextVersion">Copy</AppButton>
            </div>
            <p style="color:var(--muted); margin:0 0 8px;">
              The plain-text alternative, assembled for you. Image slices contribute their alt text,
              so run the slicer with alt text turned on to fill these in.
            </p>
            <pre
                style="white-space:pre-wrap; word-break:break-word; font-family:var(--font-mono); font-size:13px; color:var(--ink); background:var(--field); border:1px solid var(--line); border-radius:10px; padding:12px; max-height:320px; overflow:auto; margin:0;"
            >{{ textVersion || 'No text content yet. Add sections, or run the slicer with alt text turned on.' }}</pre>
          </div>

          <!-- Slice tab: the slicer, then manual image upload and swap. Mounted on first
               visit and kept, so unsaved cuts survive a switch to another tab. -->
          <div v-if="sliceVisited" v-show="activeTab === 'slice'">
            <div style="margin-top:16px;">
              <EmailSlicer />
            </div>
            <div style="margin-top:24px;">
              <h2 style="margin:0 0 12px; font-size:20px; font-weight:700;">Add or replace images by hand</h2>
              <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(320px, 1fr)); gap:18px;">
                <!-- Image Upload -->
                <div>
                  <EmailImageUpload
                      :email-alias="emailAlias"
                      :group-alias="groupAlias"
                      @error="handleUploadError"
                      @uploaded="handleImagesUploaded"
                  />
                </div>

                <!-- Image Swap -->
                <div v-if="userCanEdit">
                  <EmailImageSwap
                      :email="email"
                      :email-alias="emailAlias"
                      :group-alias="groupAlias"
                      @update:email="handleEmailUpdate"
                      @image-swapped="handleImageSwapped"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT RAIL: wraps under the main column on narrow screens -->
      <aside v-show="activeTab === 'preview'" style="flex:1 1 320px; min-width:0; display:flex; flex-direction:column; gap:20px;">

        <!-- Preflight checks, problems first -->
        <section class="console-card" style="padding:20px;">
          <div class="flex items-baseline justify-between" style="margin-bottom:14px;">
            <h2 style="margin:0; font-size:20px; font-weight:700;">Preflight</h2>
            <span style="font-weight:600;" :style="{ color: `var(--${preflight.fails ? 'bad' : preflight.warns ? 'warn' : 'good'})` }">
              {{ preflight.fails ? `${preflight.fails} to fix` : preflight.warns ? `${preflight.warns} warning${preflight.warns === 1 ? '' : 's'}` : 'All clear' }}
            </span>
          </div>
          <div style="display:flex; flex-direction:column; gap:12px;">
            <div v-for="check in sortedChecks" :key="check.id" style="display:flex; align-items:flex-start; gap:10px;">
              <UIcon
                  v-if="check.status === 'fail' || check.status === 'warn'"
                  name="i-heroicons-exclamation-triangle"
                  class="h-4 w-4"
                  style="margin-top:3px; flex-shrink:0;"
                  :style="{ color: `var(--${preflightColor(check.status)})` }"
              />
              <UIcon v-else name="i-heroicons-check" class="h-4 w-4" style="margin-top:3px; flex-shrink:0; color:var(--good);"/>
              <div style="min-width:0;">
                <div style="font-weight:600;">{{ check.label }}</div>
                <div style="color:var(--muted);">{{ check.detail }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Performance: only once there is a campaign to measure -->
        <section v-if="email?.web_id" class="console-card" style="padding:20px;">
          <div class="flex items-baseline justify-between" style="margin-bottom:14px;">
            <h2 style="margin:0; font-size:20px; font-weight:700;">Performance</h2>
            <NuxtLink :to="`/app/emails/${groupAlias}/${emailAlias}/results`" style="color:var(--acctext);">Full report</NuxtLink>
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px;">
            <div
                v-for="stat in perfTiles"
                :key="stat.label"
                style="border-radius:10px; background:var(--raise); padding:12px 14px;"
            >
              <div class="console-label">{{ stat.label }}</div>
              <div
                  class="display mono"
                  style="font-size:24px; font-weight:700; line-height:1.2;"
                  :style="{ color: stat.value != null ? 'var(--ink)' : 'var(--faint)' }"
              >
                {{ stat.value != null ? stat.value : '–' }}
              </div>
              <div v-if="stat.sub" class="mono" style="font-size:13px; color:var(--muted);">{{ stat.sub }}</div>
            </div>
          </div>
          <p v-if="!perf" style="color:var(--muted); margin:10px 0 0;">Loading campaign stats…</p>
        </section>

        <!-- Inbox preview -->
        <section class="console-card" style="padding:20px;">
          <h2 style="margin:0 0 10px; font-size:20px; font-weight:700;">Inbox preview</h2>
          <div style="border-radius:10px; background:var(--raise); padding:14px;">
            <div class="truncate" style="font-weight:600;">{{ group?.name || '–' }}</div>
            <div class="truncate">{{ email?.subject || '–' }}</div>
            <div class="truncate" style="color:var(--muted);">{{ email?.previewText || '' }}</div>
          </div>
        </section>

        <!-- Activity -->
        <section class="console-card" style="padding:20px;">
          <h2 style="margin:0 0 12px; font-size:20px; font-weight:700;">Activity</h2>
          <div style="display:flex; flex-direction:column; gap:12px;">
            <div v-if="email?.created_at" style="display:flex; align-items:flex-start; gap:10px;">
              <ConsoleStatusDot color="good" :size="8" style="margin-top:7px;"/>
              <div style="min-width:0;">
                <div style="font-weight:600;">Email created</div>
                <div class="mono" style="color:var(--muted);">{{ formatDate(email.created_at) }}</div>
              </div>
            </div>
            <div v-if="email?.updated_at" style="display:flex; align-items:flex-start; gap:10px;">
              <ConsoleStatusDot color="cyan" :size="8" style="margin-top:7px;"/>
              <div style="min-width:0;">
                <div style="font-weight:600;">Last updated</div>
                <div class="mono" style="color:var(--muted);">{{ formatDate(email.updated_at) }} · {{ formatTimeAgo(email.updated_at) }}</div>
              </div>
            </div>
            <div v-if="email?.web_id" style="display:flex; align-items:flex-start; gap:10px;">
              <ConsoleStatusDot color="accent" :size="8" style="margin-top:7px;"/>
              <div style="min-width:0;">
                <div style="font-weight:600;">Campaign created</div>
                <div class="mono" style="color:var(--muted);">#{{ email.web_id }} · {{ formatDate(email.updated_at) }}</div>
              </div>
            </div>
          </div>
        </section>
      </aside>
    </div>

    <!-- Unsaved edits: Save stays in reach while scrolling a long section list -->
    <div v-if="isValidEmail && editorRef?.dirty" class="save-bar" role="status">
      <span style="font-weight:600;">Unsaved changes</span>
      <AppButton variant="primary" small :disabled="editorRef.saving" @click="editorRef.saveEmail()">
        {{ editorRef.saving ? 'Saving…' : 'Save' }}
      </AppButton>
    </div>

    <!-- Modals (only relevant when email is valid) -->
    <div v-if="isValidEmail">

      <!-- Create campaign -->
      <AppDialog v-model:open="showCreateCampaignModal" title="Create campaign">
        <p style="margin:0; color:var(--muted);">
          This creates a campaign from this email in the workspace's email platform.
        </p>

        <label class="dialog-field">
          <span>Campaign name</span>
          <input v-model="campaignName" :disabled="creatingCampaign" class="console-input">
          <small>
            Suggested from the subject, the month and the type:
            "{{ email?.subject }}", "{{ getCurrentMonthYear() }}", "{{ getEmailTypeDescription() }}".
          </small>
        </label>

        <label class="dialog-field">
          <span>Type</span>
          <select v-model="campaignType" :disabled="creatingCampaign" class="console-input">
            <option v-for="opt in campaignTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
        </label>

        <div v-if="campaignType === 'regular'" class="dialog-field">
          <span>When it's created</span>
          <button
              type="button"
              class="dialog-option"
              :class="{ on: !scheduleCampaign }"
              :aria-pressed="!scheduleCampaign"
              :disabled="creatingCampaign"
              @click="scheduleCampaign = 0"
          >
            <b>Leave as a draft</b>
            <span>Review and send it from the email platform.</span>
          </button>
          <button
              type="button"
              class="dialog-option"
              :class="{ on: !!scheduleCampaign }"
              :aria-pressed="!!scheduleCampaign"
              :disabled="creatingCampaign"
              @click="scheduleCampaign = 1"
          >
            <b>Schedule it</b>
            <span>Pick the date and time it sends.</span>
          </button>
        </div>

        <label v-if="scheduleCampaign" class="dialog-field">
          <span>Send on</span>
          <input v-model="scheduleDateTime" :disabled="creatingCampaign" type="datetime-local" class="console-input">
        </label>

        <template #footer>
          <AppButton :disabled="creatingCampaign" @click="cancelCreateCampaign">Cancel</AppButton>
          <AppButton variant="primary" :disabled="!campaignName.trim() || creatingCampaign" @click="createCampaign">
            {{ creatingCampaign ? 'Creating…' : 'Create campaign' }}
          </AppButton>
        </template>
      </AppDialog>

      <!-- Send test -->
      <AppDialog v-model:open="showSendTestModal" title="Send a test">
        <p style="margin:0; color:var(--muted);">
          The test goes out with "[TEST]" in front of the subject, so you can check it in real inboxes.
        </p>

        <div v-if="email?.subject" style="border-radius:10px; background:var(--raise); padding:12px 14px;">
          <div style="font-weight:600;">{{ email.subject }}</div>
          <div v-if="email.previewText" style="color:var(--muted);">{{ email.previewText }}</div>
        </div>
        <div v-else role="alert" style="color:var(--bad); font-weight:600;">
          This email has no subject yet. Add one on the Details tab first.
        </div>

        <label class="dialog-field">
          <span>Send to</span>
          <textarea
              v-model="testEmailAddresses"
              :disabled="sendingTest"
              rows="3"
              class="console-input"
              placeholder="you@agency.com, client@example.com"
          />
          <small>Separate several addresses with commas.</small>
        </label>

        <template #footer>
          <AppButton :disabled="sendingTest" @click="showSendTestModal = false">Cancel</AppButton>
          <AppButton variant="primary" :disabled="!testEmailAddresses.trim() || sendingTest" @click="sendTestEmail">
            {{ sendingTest ? 'Sending…' : 'Send test' }}
          </AppButton>
        </template>
      </AppDialog>

      <!-- Duplicate -->
      <AppDialog v-model:open="showDuplicateModal" title="Duplicate this email">
        <p style="margin:0; color:var(--muted);">The copy keeps the sections, images, links and alt text. It starts as a draft with no campaign.</p>

        <label class="dialog-field">
          <span>Alias for the copy</span>
          <input v-model="duplicateAlias" :disabled="loading.duplicate" class="console-input mono" @keydown.enter="submitDuplicate">
          <small>Lowercase letters, numbers and dashes.</small>
        </label>

        <template #footer>
          <AppButton :disabled="loading.duplicate" @click="showDuplicateModal = false">Cancel</AppButton>
          <AppButton variant="primary" :disabled="!duplicateAlias.trim() || loading.duplicate" @click="submitDuplicate">
            {{ loading.duplicate ? 'Duplicating…' : 'Duplicate' }}
          </AppButton>
        </template>
      </AppDialog>
    </div>
  </div>
</template>
<script lang="ts" setup>
import CampaignAnalytics from "~/components/CampaignAnalytics.vue"
import { buildEmailText } from "~/composables/useEmailText"
import { usePackageDownloader, usePackageMaker } from "~/composables/usePackageDownloader"
import { useTimeAgo } from "@vueuse/core"
import { useEmailBuilder } from "~/composables/useEmailBuilder"
import { useEmails } from "~/composables/useEmails"
import { normalizeSections } from "~/composables/useEmailSections"
import { useUserStore } from "~/stores/users"
import { useCampaignNaming } from "~/composables/useCampaignNaming"

const { confirm: confirmDialog } = useConfirmDialog()

const route = useRoute()
const {groupAlias, emailAlias} = route.params as { groupAlias: string, emailAlias: string }
const {emailsStore, organizationsStore} = await useEmails()
const userStore = useUserStore()
const {generateCampaignName} = useCampaignNaming()

// Email and group data
const group = computed(() => organizationsStore.map?.[groupAlias] || null)
const email = computed(() => {
    if (!group.value) return null
    if (!emailsStore.map) return null
    return emailsStore.map[emailAlias] || null
})

// Slice-size detection: the builder emits both a desktop_* and mobile_* image for every
// $SIZE slice, so a size with zero files in storage renders broken in that view. Surface it
// here (mirrors the edit page) and drive the user to slice the matching design comp.
const { sizeCounts: sliceSizeCounts, loaded: slicesLoaded, fetchSlices } = useEmailSlices(groupAlias, emailAlias)
onMounted(() => fetchSlices())
const usesSlices = computed(() =>
  normalizeSections(email.value?.sections).some(
    (s) => s.type === 'image' && (s.cells || []).some((c) => /\$SIZE|(?:desktop|mobile)_\d/i.test(c.src || '')),
  ),
)
function hasDesignComp(role: 'design_desktop' | 'design_mobile') {
  return (email.value?.assets || []).some((a) => a.role === role)
}
const missingSizes = computed(() => {
  if (!slicesLoaded.value || !usesSlices.value) return []
  const out: Array<{ size: 'desktop' | 'mobile'; hasComp: boolean }> = []
  if (!sliceSizeCounts.value.desktop) out.push({ size: 'desktop', hasComp: hasDesignComp('design_desktop') })
  if (!sliceSizeCounts.value.mobile) out.push({ size: 'mobile', hasComp: hasDesignComp('design_mobile') })
  return out
})

useWorkspaceCrumbs(
  () => [{ label: 'Emails', to: '/app/emails' }, { label: email.value?.name || email.value?.subject || emailAlias }],
  () => groupAlias,
)


// Loading states
const loading = ref({
    download: false,
    duplicate: false,
    update: false,
    delete: false,
    reviewLink: false
})

const isValidEmail = computed(() => {
    const hasEmail = !!email.value
    const hasGroup = !!group.value
    return hasEmail && hasGroup
})

// Permission check
const userCanEdit = computed(() => {
    return userStore.canEditOrg(groupAlias)
})

// Review/approval status (set by external reviewers via the shareable link).
const reviewStatus = computed(() => (email.value as any)?.review_status || 'none')

// Static preflight checks over the rendered HTML (recomputes as the builder resolves).
const preflight = computed(() =>
    runEmailPreflight(builder.value?.html || '', {
        subject: email.value?.subject || '',
        previewText: email.value?.previewText || '',
    }),
)
function preflightColor(status: string) {
    return status === 'fail' ? 'bad' : status === 'warn' ? 'warn' : 'good'
}

// Problems first, so the rail opens on what needs doing.
const preflightRank: Record<string, number> = { fail: 0, warn: 1 }
const sortedChecks = computed(() =>
    [...preflight.value.checks].sort((a, b) => (preflightRank[a.status] ?? 2) - (preflightRank[b.status] ?? 2)),
)

// Tabs are path segments (…/<email>/analytics). The key leaves the tab out, so switching
// tabs keeps this page instance: the preview stays loaded and stats are not refetched.
definePageMeta({ maxWidth: 1480, key: (route) => `email-${route.params.groupAlias}-${route.params.emailAlias}` })

const tabItems = computed(() => [
    { value: "preview", label: "Preview", icon: "i-heroicons-eye" },
    { value: "slice", label: "Slice", icon: "i-heroicons-scissors" },
    { value: "sections", label: "Sections", icon: "i-heroicons-squares-2x2", count: missingCount.value || null },
    { value: "details", label: "Details", icon: "i-heroicons-adjustments-horizontal" },
    { value: "results", label: "Results", icon: "i-heroicons-chart-bar" },
])

// Links and alt texts still missing, shown as a count on the Sections tab.
const missingCount = computed(() => totalSectionIssues(normalizeSections(email.value?.sections)).count)

const activeTab = useRouteTab('tab', 'preview')

// The editor and the slicer mount on first visit and then stay mounted.
const editorRef = ref<{ dirty: boolean; saving: boolean; saveEmail: () => Promise<void> } | null>(null)
const editorVisited = ref(false)
const sliceVisited = ref(false)
// Tabs that were renamed or merged. Old links land on the tab that replaced them.
const movedTabs: Record<string, string> = { analytics: 'results', info: 'details', images: 'slice' }
watch(activeTab, (tab) => {
    if (movedTabs[tab]) {
        activeTab.value = movedTabs[tab]
        return
    }
    if (tab === 'sections' || tab === 'details') editorVisited.value = true
    if (tab === 'slice') sliceVisited.value = true
}, { immediate: true })

// Sub-bar status: unsaved edits first, then campaign sync state. The slicer writes its
// own status while its tab is showing.
watchEffect(() => {
  if (activeTab.value === 'slice') return
  if (editorRef.value?.dirty) {
    useSubbarStatus().value = { text: 'Unsaved changes', color: 'warn' }
    return
  }
  useSubbarStatus().value = email.value?.web_id
    ? { text: `Synced · campaign #${email.value.web_id}`, color: 'good' }
    : { text: 'Draft · not published', color: 'warn' }
})
// The effect above can run once more while this page is being left (the tab in the
// route changes first), which put this email's status on the next page. Clear it last.
onUnmounted(() => { useSubbarStatus().value = null })

// Campaign performance for the right rail + analytics-tab hydration.
// Mailchimp's detailed report API keys off the campaign *hash id*, but the email record
// only stores the numeric web_id — so we resolve the hash id from the org's campaign list.
const campaignHashId = ref<string | null>(null)
const perf = ref<null | {
    sent: number; opens: number; clicks: number; unsub: number
    openRate: number; clickRate: number
}>(null)

const perfTiles = computed(() => {
    const p = perf.value
    const fmtN = (n: number) => new Intl.NumberFormat().format(n || 0)
    const fmtP = (n: number) => `${((n || 0) * 100).toFixed(1)}%`
    return [
        { label: 'sent', value: p ? fmtN(p.sent) : null, sub: '' },
        { label: 'opens', value: p ? fmtN(p.opens) : null, sub: p ? fmtP(p.openRate) : '' },
        { label: 'clicks', value: p ? fmtN(p.clicks) : null, sub: p ? fmtP(p.clickRate) : '' },
        { label: 'unsub', value: p ? fmtN(p.unsub) : null, sub: '' },
    ]
})

// The analytics tab emits its detailed report — reuse the richer figures for the rail.
function onAnalyticsLoaded(report: any) {
    const c = report?.campaign
    if (!c) return
    perf.value = {
        sent: c.emails_sent,
        opens: c.opens?.opens_total ?? perf.value?.opens ?? 0,
        clicks: c.clicks?.clicks_total ?? perf.value?.clicks ?? 0,
        unsub: c.unsubscribed ?? perf.value?.unsub ?? 0,
        openRate: c.opens?.open_rate ?? perf.value?.openRate ?? 0,
        clickRate: c.clicks?.click_rate ?? perf.value?.clickRate ?? 0,
    }
}

// Resolve the campaign hash id (for the detailed Analytics tab) and populate the rail from
// the org's campaign summary list — one SWR-cached call covers both.
async function loadPerf() {
    if (!email.value?.web_id) return
    try {
        const r: any = await $fetch(
            `/api/v1/mailchimp/analytics/campaigns?groupAlias=${groupAlias}&timeframe=last12months`,
        )
        if (!r?.success || !Array.isArray(r.campaigns)) return
        const c = r.campaigns.find((x: any) => String(x.web_id) === String(email.value?.web_id))
        if (!c) return
        campaignHashId.value = c.id
        perf.value = {
            sent: c.emails_sent ?? 0,
            opens: c.opens ?? 0,
            clicks: c.clicks ?? 0,
            unsub: c.unsubscribes ?? 0,
            openRate: c.open_rate ?? 0,
            clickRate: c.click_rate ?? 0,
        }
    } catch (err) {
        console.error('Failed to load campaign performance:', err)
    }
}



// Email builder
const builder = ref({html: "", images: new Set()})
onMounted(async () => {
    if (isValidEmail.value) {
        try {
            builder.value = await useEmailBuilder({
                email: email.value,
                group: group.value
            })
        } catch (error) {
            console.error("Failed to build email:", error)
        }
    }
    loadPerf()
})

// Refresh rail stats whenever the email becomes (or stops being) a published campaign.
watch(() => email.value?.web_id, (id) => {
    if (id) {
        loadPerf()
    } else {
        perf.value = null
        campaignHashId.value = null
    }
})

// Email details for info tab

// Plain-text version assembled from the email's sections (image alt text + links).
const textVersion = computed(() => buildEmailText(email.value, group.value))

async function copyTextVersion() {
    if (!textVersion.value) return
    try {
        await navigator.clipboard.writeText(textVersion.value)
        useToast().add({ title: 'Copied', description: 'Text version copied to clipboard', color: 'success' })
    } catch {
        useToast().add({ title: 'Text version ready', description: 'Copy blocked — select the text manually', color: 'warning' })
    }
}

// Campaign details for info tab
const campaignDetails = computed(() => {
    if (!email.value || !email.value.web_id) return []

    return [
        {term: "Campaign ID", details: email.value.web_id},
        {term: "Subject Line", details: email.value.subject},
        {term: "Preview Text", details: email.value.previewText || "-"},
        {term: "Status", details: "Published to your email platform"},
        {term: "Last Updated", details: formatDate(email.value.updated_at)},
    ]
})

// Section columns for table display
const sectionColumns = [
    {
        key: "name",
        label: "Section Name",
    },
    {
        key: "type",
        label: "Type",
    },
    {
        key: "order",
        label: "Order",
    }
]

// Campaign creation
const showCreateCampaignModal = ref(false)
const campaignName = ref("")
const campaignType = ref("regular")
const scheduleCampaign = ref(false)
const scheduleDateTime = ref("")
const creatingCampaign = ref(false)

const campaignTypeOptions = [
    {label: "Regular Campaign", value: "regular"},
    {label: "A/B Test", value: "ab_split"},
    {label: "RSS Campaign", value: "rss"}
]

// Test email functionality
const showSendTestModal = ref(false)
const testEmailAddresses = ref("")
const sendingTest = ref(false)

// Duplicate email modal
const showDuplicateModal = ref(false)
const duplicateAlias = ref('')

// Action items for dropdown
const actionItems = computed(() => {
    return [
        [
            {
                label: "Download ZIP",
                icon: "i-heroicons-archive-box-arrow-down",
                disabled: loading.value.download,
                onSelect: () => downloadPackage()
            },
            {
                label: "Duplicate email",
                icon: "i-heroicons-document-duplicate",
                disabled: loading.value.duplicate,
                onSelect: () => duplicateEmail()
            },
            {
                label: "Copy review link",
                icon: "i-heroicons-link",
                disabled: loading.value.reviewLink,
                onSelect: () => copyReviewLink()
            },
            ...(email.value?.web_id
                ? [{
                    label: "Open the campaign in your email platform",
                    icon: "i-heroicons-arrow-top-right-on-square",
                    to: `https://us20.admin.mailchimp.com/campaigns/edit?id=${email.value.web_id}`,
                    target: "_blank"
                }]
                : [])
        ],
        ...(userCanEdit.value
            ? [[
                {
                    label: "Delete email",
                    icon: "i-heroicons-trash",
                    color: "error" as const,
                    disabled: loading.value.delete,
                    onSelect: () => deleteEmail()
                }
            ]]
            : [])
    ]
})

// Helper functions
function formatDate(dateString) {
    if (!dateString) return ""
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric"
    })
}

function formatTimeAgo(dateString) {
    if (!dateString) return ""
    return useTimeAgo(new Date(dateString)).value
}

function copyToClipboard(text) {
    if (!text) return

    navigator.clipboard.writeText(text)
    useToast().add({
        title: "Copied",
        description: "Text copied to clipboard",
        color: "success"
    })
}

// Send test email
async function sendTestEmail() {
    if (!testEmailAddresses.value.trim()) return
    if (!email.value?.id) {
        useToast().add({
            title: "Error",
            description: "Email information not loaded properly",
            color: "error"
        })
        return
    }

    try {
        sendingTest.value = true

        // Make the API call to send test emails
        const response = await $fetch("/api/v1/emails/send-test", {
            method: "POST",
            body: {
                emailAddresses: testEmailAddresses.value,
                emailId: email.value.id,
                groupAlias: groupAlias
            }
        })

        if (response.success) {
            // Show success message
            useToast().add({
                title: "Test emails sent",
                description: response.message || `Successfully sent test emails to ${response.recipients?.length || 0} recipients`,
                color: "success"
            })

            showSendTestModal.value = false
            testEmailAddresses.value = ""
        } else {
            throw new Error(response.error || "Failed to send test emails")
        }
    } catch (error) {
        useToast().add({
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to send test emails",
            color: "error"
        })
    } finally {
        sendingTest.value = false
    }
}


// Helper functions for campaign name display
function getCurrentMonthYear() {
    const deployDate = email.value?.deployDate ? new Date(email.value.deployDate) : new Date()
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ]
    return `${monthNames[deployDate.getMonth()]} ${deployDate.getFullYear()}`
}

function getEmailTypeDescription() {
    if (!email.value) return "Newsletter"
    const alias = email.value.alias.toLowerCase()
    const ecommerceKeywords = ["ecomm", "e-comm", "commerce", "shop", "store", "sale", "promo"]
    const isEcommerce = ecommerceKeywords.some(keyword => alias.includes(keyword))
    return isEcommerce ? "E-Commerce" : "Newsletter"
}

// Open campaign creation modal with generated name
function openCreateCampaignModal() {
    if (email.value && group.value) {
        campaignName.value = generateCampaignName(email.value, group.value)
    } else {
        console.error("Cannot generate campaign name: email or group not available")
    }
    showCreateCampaignModal.value = true
}

// Cancel campaign creation and reset form
function cancelCreateCampaign() {
    campaignName.value = ""
    campaignType.value = "regular"
    scheduleCampaign.value = false
    scheduleDateTime.value = ""
    showCreateCampaignModal.value = false
}

// Create campaign in Mailchimp
async function createCampaign() {
    if (!isValidEmail.value || !builder.value || !email.value) {
        console.error("Cannot create campaign: email or builder not available")
        return
    }

    if (!campaignName.value.trim()) return

    try {
        creatingCampaign.value = true

        const zip = await usePackageMaker({
            email: email.value,
            group: group.value,
            builder: builder.value
        })
        if (!zip) {
            throw new Error("Failed to create zip")
        }

        const campaignConfig = {
            title: campaignName.value || `${email.value.subject} - ${email.value.deployDate}`,
            subject: email.value.subject,
            previewText: email.value.previewText,
        }

        const formData = new FormData()
        formData.append("emailAlias", emailAlias)
        formData.append("title", campaignConfig.title)
        formData.append("subject", campaignConfig.subject)
        formData.append("previewText", campaignConfig.previewText)
        formData.append("file", zip, `${campaignConfig.title}.zip`)

        const campaignResult = await $fetch(`/api/v1/mailchimp/group/${groupAlias}/campaign/create`, {
            method: "POST",
            body: formData,
        })

        if (campaignResult?.web_id) {
            email.value.web_id = campaignResult.web_id

            // Save the web_id back to the email
            await $fetch(`/api/v1/emails/${groupAlias}/${emailAlias}`, {
                method: "PUT",
                body: email.value
            })

            // Show success message
            useToast().add({
                title: "Campaign created",
                description: scheduleCampaign.value
                   ? `Campaign scheduled for ${new Date(scheduleDateTime.value).toLocaleString()}`
                   : "Saved as a draft in your email platform",
                color: "success"
            })

            // Reset form and close modal
            campaignName.value = ""
            scheduleCampaign.value = false
            scheduleDateTime.value = ""
            showCreateCampaignModal.value = false

            // Refresh email data
            emailsStore.refreshEmail(emailAlias)
        } else {
            throw new Error("Campaign creation failed - no web_id returned")
        }
    } catch (error) {
        console.error("Error creating campaign:", error)
        useToast().add({
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to create campaign",
            color: "error"
        })
    } finally {
        creatingCampaign.value = false
    }
}

// Update existing Mailchimp campaign
async function updateMailchimpCampaign() {
    if (!email.value || !email.value.web_id) return

    const ok = await confirmDialog({
        title: 'Update the campaign?',
        danger: false,
        message: 'This replaces the campaign in your email platform with the current version.',
        action: 'Update'
    })
    if (!ok) return

    loading.value.update = true
    try {
        const zip = await usePackageMaker({
            email: email.value,
            group: group.value,
            builder: builder.value
        })
        if (!zip) {
            throw new Error("Failed to create zip")
        }

        const campaignConfig = {
            title: `${email.value.subject} - ${email.value.deployDate}`,
            subject: email.value.subject,
            previewText: email.value.previewText,
        }

        const formData = new FormData()
        formData.append("emailAlias", emailAlias)
        formData.append("title", campaignConfig.title)
        formData.append("subject", campaignConfig.subject)
        formData.append("previewText", campaignConfig.previewText)
        formData.append("file", zip, `${campaignConfig.title}.zip`)

        await $fetch(`/api/v1/mailchimp/group/${groupAlias}/campaign/${email.value.web_id}/update`, {
            method: "POST",
            body: formData,
        })

        useToast().add({
            title: "Campaign updated"
        })
    } catch (error) {
        console.error(error)
        useToast().add({
            title: "Error",
            description: "Failed to update campaign",
            color: "error"
        })
    } finally {
        loading.value.update = false
    }
}

// Download package function
async function downloadPackage() {
    if (!isValidEmail.value || !builder.value) {
        console.error("Cannot download package: email or builder not available")
        return
    }

    loading.value.download = true
    try {
        await usePackageDownloader({
            email: email.value,
            group: group.value,
            builder: builder.value
        })
        useToast().add({
            title: "Download complete",
            description: "ZIP package has been downloaded"
        })
    } catch (error) {
        console.error("Error downloading package:", error)
        useToast().add({
            title: "Error",
            description: "Failed to download package",
            color: "error"
        })
    } finally {
        loading.value.download = false
    }
}

// Duplicate email function — opens modal for alias entry
async function duplicateEmail() {
    if (!email.value) return
    duplicateAlias.value = `${email.value.alias}-copy`
    showDuplicateModal.value = true
}

async function submitDuplicate() {
    const newAlias = duplicateAlias.value.trim().toLowerCase()
    if (!newAlias) return

    loading.value.duplicate = true
    try {
        const newEmail = await $fetch<any>(`/api/v1/emails/${groupAlias}/${email.value!.alias}/duplicate`, {
            method: 'POST',
            body: { newAlias }
        })
        emailsStore.map[newEmail.alias] = newEmail

        // Close before leaving. This page is keyed by email, so going to the copy
        // unmounts it, and a dialog unmounted while open leaves its overlay and the
        // body's scroll lock behind.
        showDuplicateModal.value = false
        await nextTick()

        useToast().add(newEmail.imagesFailed
            ? { title: 'Email duplicated, but some images did not copy', description: 'Open the Slice tab on the copy and export the slices again.', color: 'warning' }
            : { title: 'Email duplicated' })
        await navigateTo(`/app/emails/${groupAlias}/${newEmail.alias}`)
    } catch (error: any) {
        useToast().add({
            title: 'The email was not duplicated',
            description: error?.data?.statusMessage || error?.statusMessage,
            color: 'error'
        })
    } finally {
        loading.value.duplicate = false
    }
}

// Generate + copy a shareable public review link (no login needed for the recipient).
async function copyReviewLink() {
    if (!email.value) return
    loading.value.reviewLink = true
    try {
        const res = await $fetch<{ success: boolean; url: string }>(
            `/api/v1/emails/${groupAlias}/${emailAlias}/review-link`,
            { method: 'POST', body: {} }
        )
        if (!res?.url) throw new Error('No link returned')
        try {
            await navigator.clipboard.writeText(res.url)
            useToast().add({ title: 'Review link copied', description: res.url, color: 'success' })
        } catch {
            // Clipboard blocked (e.g. insecure context) — still surface the URL.
            useToast().add({ title: 'Review link ready', description: res.url, color: 'success' })
        }
    } catch (error: any) {
        useToast().add({
            title: 'Error',
            description: error?.statusMessage || error?.data?.statusMessage || 'Failed to create review link',
            color: 'error'
        })
    } finally {
        loading.value.reviewLink = false
    }
}

// Delete email — destructive, so confirm first, then remove from store and leave.
async function deleteEmail() {
    if (!email.value) return
    const ok = await confirmDialog({
        title: 'Delete this email?',
        message: `"${email.value.name || email.value.subject || emailAlias}" will be permanently deleted. This cannot be undone.`,
        action: 'Delete'
    })
    if (!ok) return

    loading.value.delete = true
    try {
        await $fetch(`/api/v1/emails/${groupAlias}/${emailAlias}`, { method: 'DELETE' })
        delete emailsStore.map[emailAlias]
        useToast().add({ title: 'Email deleted', color: 'success' })
        navigateTo(`/app/emails/${groupAlias}`)
    } catch (error: any) {
        useToast().add({
            title: 'Error',
            description: error?.statusMessage || error?.data?.statusMessage || 'Failed to delete email',
            color: 'error'
        })
    } finally {
        loading.value.delete = false
    }
}

// Image upload handlers
function handleImagesUploaded(files: Array<{ originalName: string; savedPath: string; key: string }>) {
    useToast().add({
        title: "Images uploaded",
        description: `${files.length} image${files.length !== 1 ? "s" : ""} uploaded successfully`,
        color: "success"
    })
}

function handleUploadError(error: any) {
    console.error("Upload error:", error)
}

function handleEmailUpdate(updatedEmail: any) {
    // Update the email in the store
    if (emailsStore.map && updatedEmail.alias) {
        emailsStore.map[updatedEmail.alias] = updatedEmail
    }
}

function handleImageSwapped(_swapInfo: any) {
    // Refresh the builder to reflect the image change
    if (isValidEmail.value) {
        setTimeout(async () => {
            try {
                builder.value = await useEmailBuilder({
                    email: email.value,
                    group: group.value
                })
            } catch (error) {
                console.error("Failed to rebuild email after image swap:", error)
            }
        }, 1000)
    }
}
</script>

<style lang="scss" scoped>
.h-800 {
  height: 800px;
}

.save-bar {
  position: fixed;
  left: 50%;
  bottom: 20px;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 8px 8px 8px 18px;
  border: 1px solid var(--line2);
  border-radius: 999px;
  background: var(--panel);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  transform: translateX(-50%);
}

/* The type is stored lowercase. */
.meta-cap {
  display: inline-block;
}
.meta-cap::first-letter {
  text-transform: uppercase;
}
</style>
